import {Box, Dialog, Divider} from "@mui/material";
import './styles/task.scss'
import Prompt from "./prompt";
import Images from "./images";
import Slide from '@mui/material/Slide';
import React, {Fragment, useCallback, useRef, useMemo, useState, useEffect} from "react";
import {TransitionProps} from "@mui/material/transitions";
import Labels from "./labels";
import {useTaskFlow} from "../../context/TaskFlowContext";
import GradeOutlinedIcon from '@mui/icons-material/GradeOutlined';
import HighlightIcon from '@mui/icons-material/Highlight';
import { List, CellMeasurer, CellMeasurerCache } from 'react-virtualized';
import 'react-virtualized/styles.css';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom';

import {
    CommandType,
    ConversionType,
    FetchImagesSubTaskProcess,
    FetchImagesTaskProcess,
    GenerateTagsTaskProcess,
    taskProcess,
    TaskStatusType
} from "../../types/taskFlow.types";
import {getLocalConversionId, getLocalStorage} from "../../util/storage";
import Analysis from "./analysis";
import {useSelector} from "react-redux";
import {RootState} from "../../store";
import Chat from "./chat";

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
      children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});


export default function TaskManage () {
  const {taskProcess: taskData, loading, conversionValue, taskListProcess, conversionHistoryDetail} = useTaskFlow()
  const [endIdx, setEndIdx] = useState(20);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);

  const dialogImageInfo = useSelector((state: RootState) => state.task.dialogImageInfo);

  const { t } = useTranslation()

  const location = useLocation();

  // Add resize observer to track container dimensions
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
        setContainerHeight(entry.contentRect.height - 35);
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  const [open, setOpen] = React.useState(false);
  const [dialogInfo, setDialogInfo] = React.useState<FetchImagesSubTaskProcess | null>(null);
  const [subImages, setSubImages] = React.useState<FetchImagesSubTaskProcess[]>([])

  const handleClickOpen = () => {
    setOpen(!open);
  };

  const handleSetDialogInfo = (info: FetchImagesSubTaskProcess) => {
    setOpen(!open);
    setDialogInfo(info)
  }

    const queryParams = new URLSearchParams(location.search);
    const boardId = queryParams.get('boardId');

    const handleScroll = () => {
        const container = containerRef.current;
        if (!container) return;
        const { scrollTop, scrollHeight, clientHeight } = container;
        // 滚动到底部附近
        if (scrollHeight - scrollTop <= clientHeight + 50) {
            // 还有未加载的数据
            if (endIdx < conversionHistoryDetail.flat().length) {
                setTimeout(() => {
                    setEndIdx(prev => Math.min(prev + 20, conversionHistoryDetail.flat().length));
                }, 100); // 增加防抖避免频繁触发
            }
        }
    };

    useEffect(() => {
        const containerEl = containerRef.current;
        if (!containerEl) return;

        const debouncedScroll = debounce(handleScroll, 200);

        containerEl.addEventListener('scroll', debouncedScroll);
        return () => {
            containerEl.removeEventListener('scroll', debouncedScroll);
        };
    }, [endIdx, conversionHistoryDetail]);

    useEffect(() => {
        if (dialogImageInfo) {
            let thumbnail = taskData?.find(item => item.id === dialogImageInfo.taskId);

            if (!thumbnail) {
                conversionHistoryDetail.forEach(history => {
                    history?.forEach(item => {
                        if (item.id === dialogImageInfo.taskId) {
                            thumbnail = item as taskProcess;
                        }
                    });
                });
            }

            const thumbnailByGroup = (thumbnail && thumbnail.response && 'data' in thumbnail.response) ? thumbnail.response.data as FetchImagesSubTaskProcess[] : [];

            const thumbnailForGroup = thumbnailByGroup.filter(item => item.groupId === dialogImageInfo.groupId);

            setSubImages(thumbnailForGroup)
            setOpen(true);
            setDialogInfo(dialogImageInfo);
        }else {
            setOpen(false);
        }
    }, [dialogImageInfo]);

  const getConversionStatus = useCallback(()=>{
      const conversionId = getLocalConversionId(boardId as string);

      return taskListProcess?.find((item) => item.conversationId === conversionId)?.status;
  }, [taskListProcess])


  const conversionTemplate = (taskData: taskProcess[] | null) => {
    return taskData?.map((process, index: number) => {
      // 从通用属性中移除 key
      const commonProps = {
        taskId: process.id,
        status: process.status,
        taskResponse: process.response,
        extra: process.extra,
        conversionValue,
        triggerCmd: process.triggerCmd
      };

      // 历史记录特殊处理
      if (process.triggerCmd === null && conversionValue === ConversionType.HISTORY) {
        return (
            <div key={index} style={{
              fontSize: 13,
              marginBottom: 8,
              padding: '8px 0px 8px 0px',
              display: "flex",
              alignItems: "center"
            }}>
              <GradeOutlinedIcon style={{fontSize: 20}} />
              &nbsp;{process.content}
            </div>
        );
      }

      // 命令类型到组件的映射
      const commandComponentMap: Partial<Record<CommandType, () => React.ReactElement>> = {
        [CommandType.PARSER_TASK_CARD]: () => (
            (
                conversionValue === ConversionType.CONVERSING && process.status === TaskStatusType.EXECUTING ||
                conversionValue === ConversionType.HISTORY && process.status !== TaskStatusType.EXECUTING
            )? <Prompt
                key={index}
                {...commonProps}
                content={process.content}
                loading={loading}
            /> : <></>
        ),
          [CommandType.CHAT]: () => (
              (
                  conversionValue === ConversionType.CONVERSING && process.status === TaskStatusType.EXECUTING ||
                  conversionValue === ConversionType.HISTORY && process.status !== TaskStatusType.EXECUTING
              )? <Chat
                  key={index}
                  {...commonProps}
                  content={process.content}
                  loading={loading}
              /> : <></>
          ),
        [CommandType.ANALYSIS]: () => (
            <>
                <Analysis
                    key={index}
                    {...commonProps}
                    extra={process.extra}
                    content={process.content}
                />
                {<Divider key={`divider-${index}`} style={{color: "gray", display: 'none', fontSize: 12, marginBottom: 12}}>TASK</Divider>}
            </>
        ),
        [CommandType.GENERATE_TAGS]: () => (
            (process.status === TaskStatusType.CONFIRMING || conversionValue === ConversionType.HISTORY) ?
            <Labels
                key={index}
                {...commonProps}
                taskResponse={process.response as GenerateTagsTaskProcess}
            /> : <></>
        ),
        [CommandType.GENERATE_PLANS]: () => (
            (process.status === TaskStatusType.CONFIRMING || conversionValue === ConversionType.HISTORY) ?
            <Labels
                key={index}
                {...commonProps}
                taskResponse={process.response as GenerateTagsTaskProcess}
            /> : <></>
        ),
        [CommandType.FETCH_IMAGES]: () => (
            (process.status === TaskStatusType.CONFIRMING || conversionValue === ConversionType.HISTORY) ?
            <Images
                key={index}
                {...commonProps}
                handleClickOpen={handleClickOpen}
                handleSetDialogInfo={handleSetDialogInfo}
                extra={process.extra}
                taskResponse={process.response as FetchImagesTaskProcess}
            /> : <></>
        ),
        [CommandType.GENERATE_IMAGES]: () => (
            (process.status === TaskStatusType.CONFIRMING || conversionValue === ConversionType.HISTORY) ?
            <Images
                key={index}
                {...commonProps}
                handleClickOpen={handleClickOpen}
                singleGenerate={true}
                taskResponse={process.response as FetchImagesTaskProcess}
            /> : <></>
        )
      };

      const taskCardType = [
          CommandType.GENERATE_IMAGES,
          CommandType.FETCH_IMAGES,
          CommandType.GENERATE_PLANS,
          CommandType.GENERATE_TAGS
      ]

      // 如果卡片还在执行状态则将执行状态渲染出去
      if ((taskCardType.includes(process.triggerCmd) && (process.status === TaskStatusType.EXECUTING) || (!process.response && process.role !== 'user'))) {
          return <>
              {commandComponentMap[CommandType.PARSER_TASK_CARD]?.()}
          </>
      }

      if ( // 在历史记录中，如果卡片不是完成或者取消状态则不显示
          conversionValue === ConversionType.HISTORY &&
          taskCardType.includes(process.triggerCmd) &&
          (process.status !== TaskStatusType.COMPLETED && process.status !== TaskStatusType.CANCELED)) {
          return <></>
      }

      // 如果存在对应的组件并且有响应，则渲染组件
      return (process.response || process.triggerCmd === CommandType.PARSER_TASK_CARD) && commandComponentMap[process.triggerCmd]?.();
    })
  }

  // Memoize the items for each history item
    const historyItems = useMemo(() => {
        const reserveDetail = conversionHistoryDetail
            .map(history => (Array.isArray(history) ? [...history].filter(item => item.status !== TaskStatusType.EXECUTING).reverse() : []))
            .reverse();

        return reserveDetail?.flatMap(content => conversionTemplate(content)).slice(0, endIdx) || [];
    }, [conversionHistoryDetail, conversionTemplate, endIdx]);

  const temporary_url = process.env.REACT_APP_PROXY_TARGET ?? ''


  return <Box 
    ref={containerRef}
    className="customScrollbar" 
    style={{
      height: conversionValue === ConversionType.CONVERSING ? '85vh' : '93vh',
      width: '100%',
      position: 'relative'
    }}
  >
    {/*{conversionValue === ConversionType.HISTORY && <span className='historyLabel'>历史对话</span>}*/}

    {conversionValue === ConversionType.CONVERSING && (!taskData || !taskData.length || (getConversionStatus() === 'inactive')) && <div className='empty_label'>
      <HighlightIcon style={{fontSize: 20}} /> &nbsp;{t('ai_helper')}
    </div>}

    {conversionValue === ConversionType.HISTORY && (!conversionHistoryDetail || !conversionHistoryDetail.length) && <div className='empty_label'>
      <HighlightIcon style={{fontSize: 20}} /> &nbsp;{t('ai_helper')}
    </div>}
    
    {conversionValue === ConversionType.CONVERSING && !(getConversionStatus() === 'inactive') && conversionTemplate(taskData)}
    
    {conversionValue === ConversionType.HISTORY &&
        historyItems.map(item => (
            <div>{item}</div>
        ))
    }
    {/*<Text />*/}



    <Dialog
        open={open}
        maxWidth="lg"
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClickOpen}
        aria-describedby="alert-dialog-slide-description"
    >
     <Box className="dialogBox">
       <Box className="imageContainer">
           <div style={{
                 width: '100%', 
                 height: '620px',
                 display: 'flex',
                 justifyContent: 'center',
                 alignItems: 'center',
                 overflow: 'hidden',
                 marginTop: 5
               }}>
                   <img src={dialogInfo?.url} style={{
                     maxWidth: '100%',
                     maxHeight: '100%',
                     objectFit: 'contain'
                   }}/>
                 </div>
           <div style={{display: "flex", marginTop: 6, justifyContent: "flex-start", width: "100%", overflow: "auto"}}>
             {
                 subImages.map((subImage, index) => (
                     <div style={{marginLeft: 6}} onClick={()=>{
                         setDialogInfo({url:`${temporary_url}${subImage.url}`, description: subImage.description, tags: subImage.tags} as FetchImagesSubTaskProcess)
                     }}><img src={temporary_url+subImage.lod0} style={{border: `${temporary_url}${subImage.url}` === dialogInfo?.url ? "2px solid var(--c-accent)" : "2px solid transparent"}} key={index} className="subImage" /></div>
                 ))
             }
         </div>
       </Box>
       <Box className="infoContainer" style={{display: 'none'}}>
{/*         <h4>图片来源</h4>
         <span>
           {dialogInfo?.url}
         </span>*/}
         <h4>{t('image_description')}</h4>
         <span>{dialogInfo?.description}</span>
         <h4>{t('ai_tags')}</h4>
         <div className="tags">
           {
             dialogInfo?.tags?.map((tag, index: number) =>
                 <div className="tag" key={index}>{tag}</div>
             )
           }
         </div>
       </Box>
     </Box>
    </Dialog>

  </Box>
}